import { useEffect, useState, useMemo } from "react";
import { Col, Row, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  datetimeFormats,
  inspectionTypes,
  machineExpiredStatus,
} from "../../constants/constants";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_SortingState,
} from "material-react-table";
import { pendingDeficiences } from "../../services/dashboard";
import moment from "moment";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import ImageTag from "components/common/ImageTag";
import DownloadIocon from "assets/images/download-icon.svg";
import { nameBasedProtectedRoutes } from "router/protected";
import ProfileEdit from "assets/images/profile-edit.svg";
import { downloadDeficiencyApiCall } from "services/inspections";
import { toast } from "react-toastify";

/**
 * Create a custom component which will render upcoming inspection list section in dashboard.
 * @param props
 * @returns Upcoming Inspection component
 */
export const PendingDeficiences = (props: any) => {
  const [sortColumn, setSortColumn] = useState("device_id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: sortColumn, desc: false },
  ]); // The default sorting data to show status column sort icon
  const navigate = useNavigate();

  useEffect(() => {
    fetchPendingDeficiences({
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection:
        0 in sorting
          ? sorting[0].desc === false
            ? "asc"
            : "desc"
          : sortDirection,
      search: props.search,
    });
  }, [sorting, props.search]);

  const fetchPendingDeficiences = (params: any) => {
    setLoading(true);
    pendingDeficiences(
      {
        ...params,
      },
      props.userId
    ).then((response) => {
      setData(response.resultRows);
      setLoading(false);
    });
  };

  /* Column Definition requires for MaterialReactTable */
  type PendingDeficiences = {
    id: number;
    device_id: string;
    model_number: string;
    serial_number: number;
    job_location: string;
    inspection_type: string;
    Inspection: {
      inspection_date: string;
    };
    deficiencyData: {
      machine_status: number;
    };
  };

  // Mentioning table columns belows.
  const tableColumns = useMemo<MRT_ColumnDef<PendingDeficiences>[]>(
    () => [
      {
        accessorKey: "device_id",
        header: "DEVICE ID",
      },
      {
        accessorKey: "model_number",
        header: "MODEL NO.",
      },
      {
        accessorKey: "serial_number",
        header: "SERIAL NO.",
      },
      {
        accessorKey: "job_location",
        header: "LOCATION",
      },
      {
        accessorKey: "Inspection.inspection_date",
        header: "INSPECTION DATE",
        Cell: ({ cell, row }) => {
          return row.original?.Inspection?.inspection_date
            ? moment(row.original?.Inspection?.inspection_date).format(
                datetimeFormats.separatedDate
              )
            : "-";
        },
      },
      {
        accessorKey: "deficiencyData.machine_status",
        header: "DAYS SINCE ISSUED",
        Cell: ({ cell, row }) => {
          return row.original?.deficiencyData?.machine_status;
        },
      },
      {
        // accessorKey: "id",
        header: "ACTION",
        enableSorting: false,
        Cell: ({ cell, row }) => {
          return (
            <>
              <OverlayTrigger
                overlay={<Tooltip>Download Deficiency Report</Tooltip>}
              >
                <span
                  role="button"
                  onClick={() => downloadReport(row.original?.id)}
                  className="d-inline-block text-link me-3"
                >
                  <ImageTag
                    src={DownloadIocon}
                    alt="Download Deficiency Report"
                  />
                </span>
              </OverlayTrigger>

              <OverlayTrigger overlay={<Tooltip>Deficiency Report</Tooltip>}>
                <span
                  role="button"
                  onClick={() => openMachine(row.original?.id)}
                  className="d-inline-block"
                >
                  <ImageTag src={ProfileEdit} alt="Deficiency Report" />
                </span>
              </OverlayTrigger>
            </>
          );
        },
      },
    ],
    []
  );

  const openMachine = (machine_id: number) => {
    navigate(nameBasedProtectedRoutes.machineDetailForCustomer.path, {
      state: { machine_id: machine_id },
    });
  };

  const downloadReport = (machine_id: number) => {
    downloadDeficiencyApiCall(machine_id)
      .then((response) => {
        window.open(response.data, "_blank");
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  return (
    <Row className="mb-4" id="outstandingDeficiency">
      <Col md={12}>
        <Card className="border-0">
          <Card.Header className="border-0 bg-default bg-theme black-color fs-20 fw-bold ls-2 btlr-14 btrr-14 pe-4 ps-4 pt-11">
            PENDING DEFICIENCIES
          </Card.Header>
          <Card.Body className="pe-4 ps-4 pt-4">
            <div className="table-responsive dashboard-table-data">
              {data.length > 0 ? (
                <MaterialReactTable
                  muiTableProps={{
                    classes: {
                      root: "table custom-table border-none-table table-striped machine-custome-table",
                    },
                  }}
                  enableGlobalFilter={false}
                  enableFullScreenToggle={false}
                  enableDensityToggle={false}
                  enableHiding={false}
                  enableColumnFilters={false}
                  enablePagination={false}
                  onSortingChange={setSorting}
                  manualSorting
                  columns={tableColumns}
                  data={data}
                  state={{ sorting }}
                />
              ) : (
                <p className="text-center text-muted fst-italic mb-0 py-3">
                  No record to display
                </p>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};
